<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { formatarDataBR, isVencido } from '@/utils/useDataBR'

const emit = defineEmits(['adicionar-item', 'editar-item', 'visualizar-item', 'show-notification'])

const authStore = useAuthStore()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const lista = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({ status: [], moedas: [], instituicoes: [] })

const filtros = reactive({ busca: '', idInstituicaoFinanceira: '' })

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('LIMITES_CREDITO_COMPLETO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || authStore.pode('LIMITES_CREDITO_VISUALIZACAO'))

const colunas = computed(() => {
  const cols = [
    { field: 'idLimiteCredito', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'nomeInstituicaoFinanceira', label: 'Instituição Financeira', sortable: true, class: 'fw-medium text-body-emphasis' },
    { field: 'descricao', label: 'Descrição', sortable: true },
    { field: 'lstMoeda', label: 'Moeda', align: 'center', sortable: true, slotName: 'moeda' },
    { field: 'isRotativa', label: 'Rotativo', align: 'center', sortable: true, slotName: 'rotativo' },
    { field: 'permiteOperacoesSemSublimite', label: 'Exige Sublimite?', align: 'center', slotName: 'exigeSublimite' },
    { field: 'valorAprovado', label: 'Valor Aprovado', align: 'right', sortable: true, slotName: 'valorAprovado' },
    { field: 'saldoDisponivel', label: 'Saldo Disponível', align: 'right', sortable: true, slotName: 'saldo' },
    { field: 'dataValidade', label: 'Validade', align: 'center', sortable: true, slotName: 'validade' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstMoeda: (row) => getMoedaTexto(row.lstMoeda)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => { buscarDados(1) }

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) buscarDados(1)
  else { lista.value = []; paginacao.value.TotalCount = 0 }
})

const carregarListas = async () => {
  try {
    const [resMoedas, resInst] = await Promise.all([
      api.get('/ListasSistema/lookup/MOE'),
      api.get('/Parceiros?lstTipoPessoa=PARCEIRO_TIPO5&PageSize=500')
    ])
    listas.moedas = resMoedas.data
    listas.instituicoes = resInst.data
  } catch (e) { console.warn('Erro ao carregar listas:', e) }
}

const getMoedaTexto = (id) => {
  if (!id) return '-'
  const obj = listas.moedas.find(m => m.idListaSistemaItem === id || m.idListaClienteItem === id)
  return obj ? obj.textoLista : id
}

const getMoedaIso = (id) => {
  return getMoedaTexto(id)
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    lista.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)
    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.idInstituicaoFinanceira) params.append('idInstituicaoFinanceira', filtros.idInstituicaoFinanceira)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/LimitesCredito?${params.toString()}`)
    lista.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    erro.value = 'Não foi possível carregar os dados.'
    lista.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.idInstituicaoFinanceira = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarLimite = (item) => { emit('editar-item', item) }

const formatarMoeda = (valor) => {
  if (valor == null) return '-'
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
}

const abrirAuditoria = (item) => {
  auditoriaSelecionada.id = item.idLimiteCredito
  auditoriaSelecionada.nome = item.descricao
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Gestão de Limites de Crédito"
    subtitulo="Controle de aprovação de crédito e garantias em instituições parceiras"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    :mostrar-botao-adicionar="temPermissaoCompleta"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-5">
          <label class="form-label">Descrição ou Referência</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-4">
          <label class="form-label">Instituição Financeira</label>
          <select class="form-select" v-model="filtros.idInstituicaoFinanceira" @change="aplicarFiltros">
            <option value="">Todos os Bancos</option>
            <option v-for="b in listas.instituicoes" :key="b.idParceiro" :value="b.idParceiro">{{ b.nomeFantasia || b.razaoSocial }}</option>
          </select>
        </div>
        <div class="col-auto ms-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3 border-0 d-flex align-items-center">
      <i class="bi bi-exclamation-triangle-fill fs-4 me-3"></i> {{ erro }}
    </div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="lista"
      :loading="loading"
      empty-message="Nenhum limite de crédito encontrado."
      row-key="idLimiteCredito"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarLimite($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idLimiteCredito }}</span>
      </template>
      <template #moeda="{ row }">
        <span class="badge bg-body-secondary text-body border border-secondary-subtle" :title="getMoedaTexto(row.lstMoeda)">
          {{ getMoedaIso(row.lstMoeda) }}
        </span>
      </template>

      <template #rotativo="{ row }">
        <span v-if="row.isRotativa" class="badge text-primary-emphasis bg-primary-subtle border border-primary-subtle px-3">Sim</span>
        <span v-else class="badge text-secondary-emphasis bg-secondary-subtle border border-secondary-subtle px-3">Não</span>
      </template>

      <template #exigeSublimite="{ row }">
        <span v-if="row.permiteOperacoesSemSublimite" class="badge bg-ghost-warning">Flexível</span>
        <span v-else class="badge bg-ghost-info">Restritivo</span>
      </template>

      <template #valorAprovado="{ row }">
        {{ formatarMoeda(row.valorAprovado, row.lstMoeda) }}
      </template>

      <template #saldo="{ row }">
        <span class="fw-medium" :class="row.saldoDisponivel < 0 ? 'text-danger' : 'text-success'">
          {{ formatarMoeda(row.saldoDisponivel, row.lstMoeda) }}
        </span>
      </template>

      <template #validade="{ row }">
        <span v-if="isVencido(row.dataValidade)" class="badge badge-ghost-danger">Expirado</span>
        <span v-else class="text-body-secondary">{{ formatarDataBR(row.dataValidade) }}</span>
      </template>

      <template #acoes="{ row }">
        <div class="d-flex justify-content-end gap-1 acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon text-secondary" @click="abrirAuditoria(row)" title="Ver Histórico">
            <i class="bi bi-clock-history"></i>
          </button>
          <button class="btn-icon text-info" @click="emit('visualizar-item', row)" title="Extrato">
            <i class="bi bi-receipt-cutoff"></i>
          </button>
          <button class="btn-icon" @click="editarLimite(row)" :title="temPermissaoCompleta ? 'Editar' : 'Visualizar'">
            <i :class="temPermissaoCompleta ? 'bi bi-pencil-fill text-primary' : 'bi bi-eye-fill text-secondary'"></i>
          </button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="LimiteCredito"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    :tabelas-filhas="['LimiteCreditoModalidade']"
    :chave-estrangeira="'IdLimiteCredito'"
    contexto="tenant"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
